<template>
  <b-modal
    ref="importModal"
    title="Nhập Excel nhà cung cấp"
    size="xl"
    hide-footer
    no-close-on-backdrop
  >
    <div>
      <!-- Step 1: Download Template -->
      <b-row>
        <b-col>
          <div class="form-group">
            <span>
              <b>Bước 1:</b> Vui lòng chuẩn bị dữ liệu mẫu từ file excel đúng format, hoặc có thể tải mẫu ở
              <a
                href="https://docs.google.com/spreadsheets/d/1qKi4bcETqxKZlq-oo7xVq4_ml_2y3X_vRr1Lc_2EFHY/export?format=xlsx"
                class="text-primary"
              >
                <b>đây</b>
              </a>
            </span>
          </div>
        </b-col>
      </b-row>

      <!-- Step 2: Select Import Mode -->
      <b-row>
        <b-col>
          <b-form-group>
            <b>Bước 2: </b>
            <span>Vui lòng chọn chế độ nhập dữ liệu</span>
            <br>
            <b-form-select
              class="select-style mt-3 w-25"
              v-model="selectedMode"
              :options="importModes"
              size="sm"
              value-field="value"
              text-field="label"
              @change="onModeChange"
            >
              <template #first>
                <option
                  :value="null"
                  disabled
                >
                  -- Vui lòng chọn chế độ nhập --
                </option>
              </template>
            </b-form-select>

            <!-- Mode-specific instructions -->
            <div
              v-if="selectedMode"
              class="mt-3"
            >
              <div
                v-if="selectedMode === 'create_new'"
                class="alert alert-warning text-dark"
              >
                <i class="fas fa-info-circle mr-2 text-warning"></i>
                <strong>Chế độ tạo mới:</strong>
                <ul class="mb-0 mt-2 ml-4">
                  <li>Chỉ tạo nhà cung cấp chưa tồn tại trong hệ thống</li>
                  <li>Mã nhà cung cấp sẽ được tự động tạo nếu để trống</li>
                  <li>Nếu mã đã tồn tại, quá trình import sẽ bị dừng</li>
                </ul>
              </div>

              <div
                v-else-if="selectedMode === 'update_existing'"
                class="alert alert-warning text-dark"
              >
                <i class="fas fa-info-circle mr-2 text-warning"></i>
                <strong>Chế độ cập nhật:</strong>
                <ul class="mb-0 mt-2 ml-4">
                  <li>Chỉ cập nhật nhà cung cấp đã tồn tại</li>
                  <li>Bắt buộc phải có mã nhà cung cấp</li>
                  <li>Chỉ cập nhật các trường có giá trị (không cập nhật trường rỗng)</li>
                  <li>Nếu mã không tồn tại, quá trình import sẽ bị dừng</li>
                </ul>
              </div>

              <div
                v-else-if="selectedMode === 'create_or_update'"
                class="alert alert-warning text-dark"
              >
                <i class="fas fa-info-circle mr-2 text-warning"></i>
                <strong>Chế độ linh hoạt:</strong>
                <ul class="mb-0 mt-2 ml-4">
                  <li>Tạo mới nếu mã chưa tồn tại</li>
                  <li>Cập nhật nếu mã đã tồn tại</li>
                  <li>Phù hợp cho việc đồng bộ dữ liệu lớn</li>
                </ul>
              </div>
            </div>
          </b-form-group>
        </b-col>
      </b-row>
      
      <!-- Step 3: Upload File -->
      <b-row v-if="selectedMode">
        <b-col cols="12">
          <span><b>Bước 3:</b> Tiến hành Import</span>

          <!-- Drag and Drop Area -->
          <div
            class="drop-zone mt-3 mb-3"
            :class="{ 'drop-zone-active': isDragging }"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
            v-if="!fileName && !isProcessing"
          >
            <div class="text-center">
              <i class="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
              <p class="mb-2">Kéo thả file Excel vào đây hoặc</p>
              <b-button
                variant="primary"
                @click="$refs.fileInput.click()"
              >
                <i class="fas fa-folder-open mr-2"></i>
                Chọn từ máy tính
              </b-button>
              <p class="text-muted mt-2 mb-0">
                <small>Chấp nhận file .xlsx, .xls (tối đa 2MB)</small>
              </p>
            </div>
          </div>

          <!-- Hidden file input -->
          <input
            ref="fileInput"
            type="file"
            accept=".xlsx,.xls"
            class="d-none"
            @change="onFileSelected"
          />

          <!-- File info display -->
          <div
            v-if="fileName && !isProcessing"
            class="file-info-display"
          >
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <i class="fas fa-file-excel text-success mr-2"></i>
                <strong>{{ fileName }}</strong>
                <small class="text-muted ml-2">({{ formatFileSize(fileSize) }})</small>
              </div>
              <b-button
                variant="link"
                class="text-danger"
                @click="clearFile"
              >
                <i class="fas fa-times"></i> Xóa
              </b-button>
            </div>
          </div>

          <!-- Processing Status -->
          <div
            v-if="isProcessing"
            class="processing-status mt-4"
          >
            <b-alert
              :variant="processStatus.variant"
              show
            >
              <div class="d-flex align-items-center">
                <b-spinner
                  v-if="processStatus.loading"
                  small
                  class="mr-2"
                ></b-spinner>
                <i
                  v-else
                  :class="processStatus.icon"
                  class="mr-2"
                ></i>
                <span>{{ processStatus.message }}</span>
              </div>
            </b-alert>

            <!-- Progress bar with detailed info -->
            <b-progress
              :value="processProgress"
              :max="100"
              height="25px"
              animated
              striped
            >
              <b-progress-bar :value="processProgress">
                <span>{{ processProgress }}%</span>
              </b-progress-bar>
            </b-progress>

            <!-- Mode-specific progress details -->
            <div
              v-if="progressDetails"
              class="mt-3"
            >
              <div class="d-flex justify-content-between text-muted small">
                <span>Đã xử lý: {{ progressDetails.processed || 0 }}/{{ progressDetails.total || 0 }}</span>
                <span v-if="selectedMode === 'create_new'">
                  Tạo mới: {{ progressDetails.created || 0 }}
                </span>
                <span v-else-if="selectedMode === 'update_existing'">
                  Cập nhật: {{ progressDetails.updated || 0 }}
                </span>
                <span v-else-if="selectedMode === 'create_or_update'">
                  Tạo mới: {{ progressDetails.created || 0 }} | Cập nhật: {{ progressDetails.updated || 0 }}
                </span>
              </div>
            </div>

            <!-- Import errors -->
            <div
              v-if="importErrors.length > 0"
              class="mt-3"
            >
              <h6 class="text-danger">Lỗi import:</h6>
              <div class="error-list">
                <div
                  v-for="(error, index) in importErrors"
                  :key="index"
                  class="error-item"
                >
                  <i class="fas fa-exclamation-circle text-danger mr-2"></i>
                  <span v-if="error.row">Dòng {{ error.row }}: </span>
                  {{ getErrorMessage(error) }}
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Step 4: Preview Data -->
      <div
        class="mt-4"
        v-if="importItems.length > 0"
      >
        <hr
          class="hr-text"
          data-content="Bước 4: Xem trước dữ liệu"
        />
        <h5>
          Danh sách nhà cung cấp từ file excel (Tổng: {{ importItems.length }} nhà cung cấp)
        </h5>

        <!-- Mode-specific Summary -->
        <div class="alert alert-info">
          <div class="row">
            <div class="col-md-6">
              <h6><i class="fas fa-chart-bar"></i> Tổng quan:</h6>
              <ul class="mb-0">
                <li>Tổng số bản ghi: <strong>{{ importItems.length }}</strong></li>
                <li>Bản ghi hợp lệ: <strong class="text-success">{{ validItems.length }}</strong></li>
                <li>Bản ghi lỗi: <strong class="text-danger">{{ invalidItems.length }}</strong></li>
              </ul>
            </div>
            <div class="col-md-6">
              <h6><i class="fas fa-info-circle"></i> Chi tiết theo chế độ:</h6>
              <ul class="mb-0">
                <li v-if="selectedMode === 'create_new'">
                  Sẽ tạo mới: <strong class="text-primary">{{ itemsToCreate.length }}</strong> nhà cung cấp
                </li>
                <li v-else-if="selectedMode === 'update_existing'">
                  Sẽ cập nhật: <strong class="text-warning">{{ itemsToUpdate.length }}</strong> nhà cung cấp
                </li>
                <li v-else-if="selectedMode === 'create_or_update'">
                  Tạo mới: <strong class="text-primary">{{ itemsToCreate.length }}</strong> |
                  Cập nhật: <strong class="text-warning">{{ itemsToUpdate.length }}</strong>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Validation Summary -->
        <div
          v-if="validationSummary.hasErrors"
          class="alert alert-warning"
        >
          <h6><i class="fas fa-exclamation-triangle"></i> Cảnh báo:</h6>
          <ul class="mb-0">
            <li v-if="validationSummary.invalidRows > 0">
              {{ validationSummary.invalidRows }} dòng có lỗi dữ liệu
            </li>
            <li v-if="validationSummary.duplicateNames > 0">
              {{ validationSummary.duplicateNames }} tên nhà cung cấp bị trùng lặp
            </li>
            <li v-if="validationSummary.duplicatePhones > 0">
              {{ validationSummary.duplicatePhones }} số điện thoại bị trùng lặp
            </li>
            <li v-if="validationSummary.missingCodes > 0 && selectedMode === 'update_existing'">
              {{ validationSummary.missingCodes }} dòng thiếu mã nhà cung cấp (bắt buộc cho cập nhật)
            </li>
          </ul>
        </div>

        <!-- Data Preview Table -->
        <b-table
          :items="previewItems"
          :fields="previewFields"
          striped
          hover
          small
          responsive
          class="mt-3"
        >
          <template v-slot:cell(index)="row">
            {{ row.index + 1 }}
          </template>

          <template v-slot:cell(operationType)="row">
            <span
              v-if="row.item.operationType === 'create'"
              class="badge badge-primary"
            >
              <i class="fas fa-plus-circle"></i> Tạo mới
            </span>
            <span
              v-else-if="row.item.operationType === 'update'"
              class="badge badge-warning"
            >
              <i class="fas fa-edit"></i> Cập nhật
            </span>
            <span
              v-else
              class="badge badge-secondary"
            >
              <i class="fas fa-question-circle"></i> Chưa xác định
            </span>
          </template>

          <template v-slot:cell(code)="row">
            <span v-if="row.item.code">{{ row.item.code }}</span>
            <span
              v-else
              class="text-muted font-italic"
            >
              <small>{{ selectedMode === 'update_existing' ? '(Thiếu mã)' : '(Tự động tạo)' }}</small>
            </span>
          </template>

          <template v-slot:cell(name)="row">
            {{ row.item.name }}
          </template>

          <template v-slot:cell(type)="row">
            <span v-if="row.item.type === 1 || row.item.type === '1'">Cá nhân</span>
            <span v-else-if="row.item.type === 2 || row.item.type === '2'">Doanh nghiệp</span>
            <span
              v-else
              class="text-muted"
            >{{ row.item.type }}</span>
          </template>

          <template v-slot:cell(phoneNo)="row">
            {{ row.item.phoneNo }}
          </template>

          <template v-slot:cell(email)="row">
            {{ row.item.email }}
          </template>

          <template v-slot:cell(status)="row">
            <span
              v-if="row.item.status === 1 || row.item.status === '1'"
              class="badge badge-success"
            >Đang giao dịch</span>
            <span
              v-else-if="row.item.status === 2 || row.item.status === '2'"
              class="badge badge-danger"
            >Ngưng giao dịch</span>
            <span
              v-else
              class="text-muted"
            >{{ row.item.status }}</span>
          </template>

          <template v-slot:cell(isValid)="row">
            <span
              v-if="row.item.isValid"
              class="badge badge-success"
            >
              <i class="fas fa-check"></i> Hợp lệ
            </span>
            <span
              v-else
              class="badge badge-danger"
              v-b-tooltip.hover
              :title="row.item.errors ? row.item.errors.join(', ') : ''"
            >
              <i class="fas fa-times"></i> Lỗi
            </span>
          </template>
        </b-table>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-end mt-4">
          <b-button
            variant="secondary"
            class="mr-2"
            @click="clearFile"
          >
            Hủy
          </b-button>
          <b-button
            variant="primary"
            @click="confirmImport"
            :disabled="!canImport || isImporting"
          >
            <b-spinner
              small
              v-if="isImporting"
              class="mr-1"
            ></b-spinner>
            {{ getImportButtonText() }}
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { makeToastFaile, makeToastSuccess } from '@/utils/common';
// import { createFile, getFileName } from '@/utils/file-download';
import { ProviderService } from '../service/provider.service';

export default {
  name: 'ProviderImportModal',
  data() {
    return {
      // File handling
      fileName: '',
      fileSize: 0,
      selectedFile: null,
      isDragging: false,

      // Import process
      importItems: [],
      isImporting: false,
      isProcessing: false,
      processProgress: 0,
      jobId: null,
      eventSource: null,

      // Status tracking
      processStatus: {
        message: '',
        variant: 'info',
        loading: false,
        icon: 'fas fa-info-circle',
      },
      importErrors: [],
      progressDetails: null,

      // Import mode
      selectedMode: null,
      importModes: [
        {
          value: 'create_new',
          label: 'Tạo mới nhà cung cấp',
          description: 'Chỉ tạo nhà cung cấp chưa tồn tại',
        },
        {
          value: 'update_existing',
          label: 'Cập nhật nhà cung cấp',
          description: 'Chỉ cập nhật nhà cung cấp đã tồn tại',
        },
        {
          value: 'create_or_update',
          label: 'Linh hoạt (Tạo mới hoặc Cập nhật)',
          description: 'Tự động tạo mới hoặc cập nhật dựa trên mã',
        },
      ],

      // Table configuration
      previewFields: [
        {
          key: 'index',
          label: 'STT',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'operationType',
          label: 'Thao tác',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        { key: 'code', label: 'Mã NCC' },
        { key: 'name', label: 'Tên nhà cung cấp' },
        { key: 'type', label: 'Loại' },
        { key: 'phoneNo', label: 'Điện thoại' },
        { key: 'email', label: 'Email' },
        {
          key: 'status',
          label: 'Trạng thái',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'isValid',
          label: 'Kiểm tra',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
      ],

      // Cached supplier codes for validation
      existingSupplierCodes: new Set(),
    };
  },
  computed: {
    previewItems() {
      return this.importItems.slice(0, 50); // Show only first 50 items for performance
    },

    validItems() {
      return this.importItems.filter((item) => item.isValid);
    },

    invalidItems() {
      return this.importItems.filter((item) => !item.isValid);
    },

    canImport() {
      return this.validItems.length > 0;
    },

    validationSummary() {
      const summary = {
        hasErrors: false,
        invalidRows: 0,
        duplicateNames: 0,
        duplicatePhones: 0,
        missingCodes: 0,
      };

      summary.invalidRows = this.importItems.filter(
        (item) => !item.isValid,
      ).length;

      // Check for duplicate names
      const names = this.importItems
        .map((item) => item.name?.toLowerCase().trim())
        .filter(Boolean);
      const uniqueNames = new Set(names);
      summary.duplicateNames = names.length - uniqueNames.size;

      // Check for duplicate phone numbers
      const phones = this.importItems
        .map((item) => item.phoneNo?.trim())
        .filter(Boolean);
      const uniquePhones = new Set(phones);
      summary.duplicatePhones = phones.length - uniquePhones.size;

      // Check for missing codes
      const missingCodes = this.importItems.filter(
        (item) => !item.code && this.selectedMode === 'update_existing',
      ).length;
      summary.missingCodes = missingCodes;

      summary.hasErrors =
        summary.invalidRows > 0 ||
        summary.duplicateNames > 0 ||
        summary.duplicatePhones > 0 ||
        summary.missingCodes > 0;

      return summary;
    },

    itemsToCreate() {
      return this.importItems.filter((item) => item.operationType === 'create');
    },

    itemsToUpdate() {
      return this.importItems.filter((item) => item.operationType === 'update');
    },
  },
  destroyed() {
    // Clean up SSE connection when component is destroyed
    this.cleanup();
  },
  methods: {
    showModal() {
      this.resetModal();
      this.$refs.importModal.show();
    },

    hideModal() {
      this.cleanup();
      this.$refs.importModal.hide();
    },

    resetModal() {
      this.fileName = '';
      this.fileSize = 0;
      this.selectedFile = null;
      this.importItems = [];
      this.isImporting = false;
      this.isProcessing = false;
      this.processProgress = 0;
      this.jobId = null;
      this.importErrors = [];
      this.processStatus = {
        message: '',
        variant: 'info',
        loading: false,
        icon: 'fas fa-info-circle',
      };
      this.selectedMode = null;
      this.progressDetails = null;
      this.existingSupplierCodes.clear();
    },

    cleanup() {
      // Close SSE connection if exists
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
      this.resetModal();
    },

    // File handling methods
    onDragOver() {
      this.isDragging = true;
    },

    onDragLeave() {
      this.isDragging = false;
    },

    onDrop(event) {
      this.isDragging = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    },

    onFileSelected(event) {
      const files = event.target.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    },

    handleFile(file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
      ];
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name
        .substring(file.name.lastIndexOf('.'))
        .toLowerCase();

      if (
        !validTypes.includes(file.type) &&
        !validExtensions.includes(fileExtension)
      ) {
        makeToastFaile('File phải có định dạng .xlsx hoặc .xls');
        return;
      }

      // Validate file size (2MB max)
      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        makeToastFaile('Kích thước file không được vượt quá 2MB');
        return;
      }

      this.selectedFile = file;
      this.fileName = file.name;
      this.fileSize = file.size;

      // Auto start import
      this.startImport();
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    clearFile() {
      this.fileName = '';
      this.fileSize = 0;
      this.selectedFile = null;
      this.importItems = [];
      this.isProcessing = false;
      this.processProgress = 0;
      this.importErrors = [];

      // Reset file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    // async downloadTemplate() {
    //   try {
    //     const response = await ProviderService.downloadTemplate();
    //     const fileName = getFileName(response) || 'mau-nha-cung-cap.xlsx';
    //     createFile(response.data, fileName);
    //     makeToastSuccess('Tải mẫu Excel thành công');
    //   } catch (error) {
    //     console.error('Download template error:', error);
    //     makeToastFaile('Không thể tải mẫu Excel');
    //   }
    // },

    // Import process methods
    async startImport() {
      if (!this.selectedFile) {
        makeToastFaile('Vui lòng chọn file để import');
        return;
      }

      if (!this.selectedMode) {
        makeToastFaile('Vui lòng chọn chế độ nhập dữ liệu');
        return;
      }

      this.isProcessing = true;
      this.processProgress = 10;
      this.importErrors = [];
      this.updateProcessStatus('Đang tải file lên...', 'info', true);

      try {
        // Upload file and initiate import with mode
        const response = await ProviderService.importWithMode(
          this.selectedFile,
          this.selectedMode,
        );

        if (response.data.data.jobId) {
          this.jobId = response.data.data.jobId;
          this.processProgress = 30;
          this.updateProcessStatus(
            'File đã được xếp hàng để xử lý...',
            'info',
            true,
          );

          // Subscribe to SSE for real-time updates
          this.subscribeToImportStatus(response.data.data.jobId);
        } else {
          throw new Error('Không nhận được mã công việc từ server');
        }
      } catch (error) {
        console.error('Import initiation error:', error);
        this.handleImportError(error);
      }
    },

    subscribeToImportStatus(jobId) {
      if (this.eventSource) {
        this.eventSource.close();
      }

      // Subscribe to SSE updates
      this.eventSource = ProviderService.subscribeToImportStatus(
        jobId,
        (data) => this.handleImportUpdate(data),
        (error) => this.handleSSEError(error, jobId),
      );
    },

    handleImportUpdate(data) {
      // Extract progress details if available
      if (data.progress) {
        this.progressDetails = {
          processed: data.progress.processed || 0,
          total: data.progress.total || 0,
          created: data.progress.created || 0,
          updated: data.progress.updated || 0,
        };
      }

      switch (data.status) {
        case 'processing': {
          this.processProgress = 60;
          this.updateProcessStatus('Đang xử lý file...', 'info', true);
          break;
        }

        case 'validating': {
          this.processProgress = 80;
          this.updateProcessStatus('Đang kiểm tra dữ liệu...', 'info', true);
          break;
        }

        case 'success':
        case 'completed': {
          this.processProgress = 100;
          const successMessage = this.getSuccessMessage(data);
          this.updateProcessStatus(
            successMessage,
            'success',
            false,
            'fas fa-check-circle',
          );
          makeToastSuccess(successMessage);

          // Refresh provider list after 2 seconds
          setTimeout(() => {
            this.$emit('import-success');
            this.hideModal();
          }, 2000);
          break;
        }

        case 'failed': {
          this.processProgress = 100;
          this.updateProcessStatus(
            data.message || 'Import thất bại',
            'danger',
            false,
            'fas fa-times-circle',
          );

          if (data.errors && data.errors.length > 0) {
            this.importErrors = data.errors;
          }

          makeToastFaile(data.message || 'Import thất bại');
          break;
        }

        default:
          console.log('Unknown import status:', data.status);
      }
    },

    getSuccessMessage(data) {
      if (data.message) {
        return data.message;
      }

      const details = this.progressDetails || {};
      if (this.selectedMode === 'create_new') {
        return `Import thành công ${details.created || 0} nhà cung cấp mới`;
      } else if (this.selectedMode === 'update_existing') {
        return `Cập nhật thành công ${details.updated || 0} nhà cung cấp`;
      } else if (this.selectedMode === 'create_or_update') {
        return `Import thành công: ${details.created || 0} tạo mới, ${
          details.updated || 0
        } cập nhật`;
      }

      return 'Import thành công!';
    },

    handleSSEError(error, jobId) {
      console.error('SSE connection error:', error);

      // Fallback to polling
      if (jobId) {
        this.pollImportStatus(jobId);
      } else {
        this.handleImportError(new Error('Mất kết nối với server'));
      }
    },

    async pollImportStatus(jobId) {
      const maxAttempts = 30;
      let attempts = 0;

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          // if (!this.jobId) {
          //   clearInterval(pollInterval);
          //   return;
          // }
          const response = await ProviderService.getImportStatus(jobId);
          const data = response.data.data;

          if (data.status === 'completed') {
            clearInterval(pollInterval);
            this.handleImportUpdate({
              status: 'success',
              message: `Import thành công ${data.processedRecords} bản ghi`,
            });
          } else if (data.status === 'failed') {
            clearInterval(pollInterval);
            const errors = data.errorDetails?.errors || [];
            if (errors.length > 0) {
              this.handleImportUpdate({
                status: 'failed',
                message: 'Import thất bại',
                errors: data.errorDetails?.errors || [],
              });
            }
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            this.handleImportError(new Error('Quá thời gian chờ xử lý'));
          }
        } catch (error) {
          clearInterval(pollInterval);
          this.handleImportError(error);
        }
      }, 2000);
    },

    handleImportError(error) {
      this.isProcessing = false;
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.message ||
        'Có lỗi xảy ra khi import';

      this.updateProcessStatus(
        errorMessage,
        'danger',
        false,
        'fas fa-exclamation-triangle',
      );
      makeToastFaile(errorMessage);
    },

    updateProcessStatus(message, variant, loading, icon = null) {
      this.processStatus = {
        message,
        variant,
        loading,
        icon: icon || (loading ? null : this.getIconForVariant(variant)),
      };
    },

    getIconForVariant(variant) {
      const icons = {
        success: 'fas fa-check-circle',
        danger: 'fas fa-times-circle',
        warning: 'fas fa-exclamation-triangle',
        info: 'fas fa-info-circle',
      };
      return icons[variant] || 'fas fa-info-circle';
    },

    // Legacy methods for preview (if needed)
    handleFileUpload(data, fileName) {
      this.fileName = fileName;
      this.importItems = data.rows || [];
      // this.validateImportData();
    },

    // async validateImportData() {
    //   // First, collect all supplier codes for existence check
    //   const supplierCodesToCheck = this.importItems
    //     .map((item) => item.code)
    //     .filter((code) => code && code.trim());

    //   // Check existing codes if needed
    //   if (
    //     supplierCodesToCheck.length > 0 &&
    //     (this.selectedMode === 'update_existing' ||
    //       this.selectedMode === 'create_or_update')
    //   ) {
    //     await this.checkExistingSupplierCodes(supplierCodesToCheck);
    //   }

    //   this.importItems = this.importItems.map((item, index) => {
    //     const errors = [];
    //     let operationType = 'unknown';

    //     // Mode-specific validation
    //     if (this.selectedMode === 'create_new') {
    //       // For create mode, code should not exist
    //       if (item.code && this.existingSupplierCodes.has(item.code.trim())) {
    //         errors.push(`Mã nhà cung cấp "${item.code}" đã tồn tại`);
    //       }
    //       operationType = 'create';
    //     } else if (this.selectedMode === 'update_existing') {
    //       // For update mode, code is required and must exist
    //       if (!item.code || !item.code.trim()) {
    //         errors.push('Mã nhà cung cấp là bắt buộc cho chế độ cập nhật');
    //       } else if (!this.existingSupplierCodes.has(item.code.trim())) {
    //         errors.push(`Mã nhà cung cấp "${item.code}" không tồn tại`);
    //       } else {
    //         operationType = 'update';
    //       }
    //     } else if (this.selectedMode === 'create_or_update') {
    //       // For flexible mode, determine operation based on existence
    //       if (item.code && this.existingSupplierCodes.has(item.code.trim())) {
    //         operationType = 'update';
    //       } else {
    //         operationType = 'create';
    //       }
    //     }

    //     // Common validation for all modes
    //     if (operationType === 'create' || !item.code) {
    //       // For new records, name is required
    //       if (!item.name || item.name.trim().length < 3) {
    //         errors.push('Tên nhà cung cấp không hợp lệ (tối thiểu 3 ký tự)');
    //       }

    //       if (
    //         !item.phoneNo ||
    //         !/^[0-9]{10,11}$/.test(item.phoneNo.replace(/\s/g, ''))
    //       ) {
    //         errors.push('Số điện thoại không hợp lệ');
    //       }
    //     }

    //     // Validate type
    //     if (item.type && !['1', '2', 1, 2].includes(item.type)) {
    //       errors.push(
    //         'Loại nhà cung cấp không hợp lệ (1: Cá nhân, 2: Doanh nghiệp)',
    //       );
    //     }

    //     // Validate status
    //     if (item.status && !['1', '2', 1, 2].includes(item.status)) {
    //       errors.push(
    //         'Trạng thái không hợp lệ (1: Đang giao dịch, 2: Ngưng giao dịch)',
    //       );
    //     }

    //     // Validate email if provided
    //     if (item.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item.email)) {
    //       errors.push('Email không hợp lệ');
    //     }

    //     return {
    //       ...item,
    //       isValid: errors.length === 0,
    //       errors,
    //       rowIndex: index + 1,
    //       operationType,
    //     };
    //   });
    // },

    async checkExistingSupplierCodes(codes) {
      try {
        // This is a placeholder - you need to implement the actual API call
        // to check which supplier codes already exist
        const response = await ProviderService.checkExistingCodes(codes);
        if (response.data && response.data.data) {
          this.existingSupplierCodes = new Set(response.data.data);
        }
      } catch (error) {
        console.error('Error checking existing supplier codes:', error);
        // For now, assume none exist if API fails
        this.existingSupplierCodes.clear();
      }
    },

    async confirmImport() {
      if (!this.canImport) {
        makeToastFaile('Không có dữ liệu hợp lệ để nhập');
        return;
      }

      this.isImporting = true;

      try {
        // Create FormData for bulk import
        const validData = this.validItems.map((item) => ({
          name: item.name.trim(),
          phoneNo: item.phoneNo?.trim() || null,
          email: item.email?.trim() || null,
          type: parseInt(item.type) || 2,
          status: parseInt(item.status) || 1,
          taxNo: item.taxNo?.trim() || null,
          identityCard: item.identityCard?.trim() || null,
          address: item.address?.trim() || null,
          bank: item.bank?.trim() || null,
          bankBranch: item.bankBranch?.trim() || null,
          bankAccountNo: item.bankAccountNo?.trim() || null,
          bankAccountName: item.bankAccountName?.trim() || null,
          representative: item.representative?.trim() || null,
          repPosition: item.repPosition?.trim() || null,
          repPhoneNo: item.repPhoneNo?.trim() || null,
          creditLimit: item.creditLimit ? parseFloat(item.creditLimit) : null,
          paymentTermsDays: item.paymentTermsDays
            ? parseInt(item.paymentTermsDays)
            : null,
        }));

        const response = await ProviderService.bulkCreate(validData);

        if (response.data.success) {
          makeToastSuccess(`Nhập thành công ${validData.length} nhà cung cấp`);
          this.hideModal();
          this.clearFile();
          this.$emit('import-success');
        } else {
          makeToastFaile(
            response.data.message || 'Có lỗi xảy ra khi nhập dữ liệu',
          );
        }
      } catch (error) {
        console.error('Import error:', error);
        const errorMessage =
          error.response?.data?.message || 'Có lỗi xảy ra khi nhập dữ liệu';
        makeToastFaile(errorMessage);
      } finally {
        this.isImporting = false;
      }
    },

    onModeChange() {
      // Clear existing data when mode changes
      this.clearFile();
      this.importItems = [];
      this.importErrors = [];
      this.progressDetails = null;
    },

    getImportButtonText() {
      if (this.isImporting) {
        return 'Đang nhập...';
      }

      if (!this.canImport) {
        return 'Không có dữ liệu hợp lệ để nhập';
      }

      if (this.selectedMode === 'create_new') {
        return `Tạo mới ${this.itemsToCreate.length} nhà cung cấp`;
      } else if (this.selectedMode === 'update_existing') {
        return `Cập nhật ${this.itemsToUpdate.length} nhà cung cấp`;
      } else if (this.selectedMode === 'create_or_update') {
        const createCount = this.itemsToCreate.length;
        const updateCount = this.itemsToUpdate.length;
        return `Xử lý ${this.validItems.length} NCC (${createCount} mới, ${updateCount} cập nhật)`;
      }

      return `Nhập ${this.validItems.length} nhà cung cấp hợp lệ`;
    },

    getErrorMessage(error) {
      // Handle mode-specific error codes
      const errorCode = error.code || error.errorCode;

      if (errorCode === 'SUPPLIER_NOT_FOUND') {
        return `Mã nhà cung cấp "${error.supplierCode}" không tồn tại trong hệ thống`;
      } else if (errorCode === 'SUPPLIER_ALREADY_EXISTS') {
        return `Mã nhà cung cấp "${error.supplierCode}" đã tồn tại`;
      } else if (errorCode === 'INVALID_IMPORT_MODE') {
        return 'Chế độ import không hợp lệ';
      }

      // Default error message
      return error.error || error.message || 'Lỗi không xác định';
    },
  },
};
</script>

<style scoped>
.hr-text {
  position: relative;
  border: none;
  height: 1px;
  background: #ddd;
  margin: 20px 0;
}

.hr-text:before {
  content: attr(data-content);
  position: absolute;
  top: -10px;
  left: 10px;
  background: white;
  padding: 0 10px;
  font-weight: bold;
  color: #666;
}

.form-group label {
  font-weight: 600;
  color: #6c757d;
}

/* Drag and Drop Zone */
.drop-zone {
  border: 2px dashed #e4e6ef;
  border-radius: 0.42rem;
  padding: 3rem 2rem;
  transition: all 0.3s ease;
  background-color: #f8f9fa;
}

.drop-zone:hover {
  border-color: #3699ff;
  background-color: #f3f6f9;
}

.drop-zone-active {
  border-color: #3699ff;
  background-color: #e1f0ff;
}

/* File Info Display */
.file-info-display {
  border: 1px solid #e4e6ef;
  border-radius: 0.42rem;
  padding: 1rem;
  background-color: #f3f6f9;
  margin-top: 1rem;
}

/* Processing Status */
.processing-status {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Error List */
.error-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #f3e5e5;
  border-radius: 0.25rem;
  padding: 0.5rem;
  background-color: #fff5f5;
}

.error-item {
  padding: 0.5rem;
  border-bottom: 1px solid #f3e5e5;
  font-size: 0.875rem;
}

.error-item:last-child {
  border-bottom: none;
}

/* Progress Bar Enhancement */
.progress-bar {
  font-weight: 600;
  font-size: 0.75rem;
}

/* Alert Enhancement */
.alert {
  border: none;
  box-shadow: 0 0 20px 0 rgba(76, 87, 125, 0.05);
}

/* Mode-specific Alert Styling */
.alert-info {
  background-color: #e1f0ff;
  border-left: 4px solid #3699ff;
  color: #004085;
}

.alert-warning {
  background-color: #fff4de;
  border-left: 4px solid #ffa800;
  color: #856404;
}

.alert-success {
  background-color: #c9f7f5;
  border-left: 4px solid #1bc5bd;
  color: #0c5460;
}

/* Table Enhancement */
.table td {
  vertical-align: middle;
}

/* Badge Enhancement */
.badge {
  padding: 0.4rem 0.6rem;
  font-weight: 500;
  font-size: 0.75rem;
}

/* Operation Type Badges */
.badge-primary {
  background-color: #3699ff;
}

.badge-warning {
  background-color: #ffa800;
  color: #212529;
}

.badge-secondary {
  background-color: #e4e6ef;
  color: #7e8299;
}

/* Tooltip Enhancement */
.b-tooltip {
  font-size: 0.875rem;
}

/* Summary Cards */
.alert .row ul {
  padding-left: 1.5rem;
}

.alert .row ul li {
  margin-bottom: 0.25rem;
}

/* Action Button Enhancement */
.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
